import * as React from "react";
import { useLocation } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import WidgetsIcon from "@mui/icons-material/Widgets";
import SideNav from "./SideNav";

/**
 * Mobile navigation - the same SideNav, inside a drawer.
 *
 * Replaces the two hand-maintained ResRightSidebar copies. Those had drifted:
 * the admin one was never rendered at all and still pointed at /dashboard/*
 * routes, so the teachers panel shipped without mobile navigation.
 *
 * The drawer is always expanded - icons-only makes no sense in an overlay that
 * already has the full width to spare.
 *
 * Note the drawer portals to document.body, outside .admin / .dashboard. The
 * accent still resolves because SectionThemeScope (src/theme/section.js) mirrors
 * the section class onto <body>.
 */
export function SideNavDrawer({ items, footerItems, brand, footer, ariaLabel }) {
    const [open, setOpen] = React.useState(false);
    const { pathname } = useLocation();

    // Close on navigation, including browser back/forward.
    React.useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <>
            {/* Carries its own surface/border/shadow: it is positioned fixed
                over page content, not sitting on the page background. */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="باز کردن منو"
                aria-expanded={open}
                className="flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-soft border border-rule-subtle bg-surface text-ink-muted shadow-card transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <WidgetsIcon sx={{ fontSize: "2.4rem" }} />
            </button>

            {/* anchor="right" places the Paper on the physical right, because
                without stylis-plugin-rtl nothing mirrors its `right: 0`.

                SlideProps.direction then has to be corrected by hand. Drawer
                computes the slide from getAnchor(), which DOES flip under
                theme.direction="rtl", while the Paper's CSS uses the raw anchor
                - so the two disagree and the panel animates in from the far
                side. SlideProps is spread after Drawer's own `direction`, so
                this wins. direction="left" means "enters from the right".

                Was SwipeableDrawer. Its drag handling routes through the same
                flipped anchor in four more places, so the swipe edge and the
                drag transform were inverted too; plain Drawer has no drag math
                to get wrong. See the note in the chat about installing
                stylis-plugin-rtl, which is the real fix for all of this. */}
            <Drawer
                anchor="right"
                SlideProps={{ direction: "left" }}
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: "26rem",
                        backgroundColor: "rgb(var(--c-surface))",
                        backgroundImage: "none",
                    },
                }}
            >
                <SideNav
                    items={items}
                    footerItems={footerItems}
                    brand={brand}
                    footer={footer}
                    collapsed={false}
                    ariaLabel={ariaLabel}
                />
            </Drawer>
        </>
    );
}

export default SideNavDrawer;
